import { useEffect, useState } from 'react';
import { object } from 'yup';
import * as yup from 'yup';
import { money } from '~hooks/useHelpers';
import { detectFileType } from '~services/utils/validationFile';

const useCredit = ({limite, atualVC = 0, atualPagueFlex = 0, atualVloz = 0, limiteF, tamanhoMaximoUpload = 30}) => {
    const [valuess, setValue] = useState({ name: '', value: '' });
    const [maximumSize, setMaximumSize] = useState(20);

    useEffect(() => {
        if(!isNaN(tamanhoMaximoUpload)) setMaximumSize(tamanhoMaximoUpload);
    }, [tamanhoMaximoUpload])

    const toDecimalByString = (val) => {
        const roundInt = Math.round(parseFloat(val));
        const number = roundInt.toString().replace(/[^\d,]/g, "");
        return parseFloat(`${number.substring(0, number.length - 2)}.${number.substring(number.length - 2, number.length)}`);
    }

    const allowedExtensions = {
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      png: "image/png",
      gif: "image/gif",
      jpeg: "image/jpeg",
      jpg: "image/jpeg",
      pdf: "application/pdf",
      csv: "text/csv",
      odt: "application/vnd.oasis.opendocument.text",
      zip: "application/zip",
      msg: "application/vnd.ms-outlook",
      eml: "message/rfc822",
    };

    const validateFile = async (file) => {
        if(file !== undefined){
            const name = file.name ?? "Arquivo";
            const realType = await detectFileType({file, allowedExtensions});

            const allowedValues = Object.keys(allowedExtensions).map(k => k.toLowerCase());
            const allowed = allowedValues.includes(realType);

            let messages = {};

            if(!allowed){
                messages = {
                    success: false,
                    message: `${name}: Inválido.\nExtensão não permitida. Extensões permitidas ${Object.values(allowedExtensions).filter(o => o.startsWith(".")).join(', ')}`,
                }
            }

            return Object.keys(messages).length ? messages : {
                success: true
            }
        }else{
            return {
                success: false,
                message: "Anexe um documento válido para prosseguir",
            }
        }
    }

    const toCheckRequiredFields = (context) => {
        const { limiteVotorantim, limiteFlex, limiteVloz } = context.parent;

        const decimalLimiteVot = toDecimalByString(limiteVotorantim);
        const decimalLimiteFlex = toDecimalByString(limiteFlex);
        const decimalLimiteVloz = toDecimalByString(limiteVloz);

        return decimalLimiteVot > 0 || decimalLimiteFlex > 0 || decimalLimiteVloz > 0;
    };

    const validateLimits = (limiteVotorantim, limiteFlex, limiteVloz) => {
      const decimalLimiteVot = toDecimalByString(limiteVotorantim);

      if (decimalLimiteVot > 0 && decimalLimiteVot < atualVC)
        return false;

      const decimalLimiteFlex = toDecimalByString(limiteFlex);

      if (decimalLimiteFlex > 0 && decimalLimiteFlex < atualPagueFlex)
        return false;

      const decimalLimiteVloz = toDecimalByString(limiteVloz);

      if (decimalLimiteVloz > 0 && decimalLimiteVloz < atualVloz)
        return false;

      return true;
    }

    const requiredDocuments = (context, values) => {
        const { limiteVotorantim, limiteFlex } = context.parent;

        const decimalLimiteVot = toDecimalByString(limiteVotorantim);
        const decimalLimiteFlex = toDecimalByString(limiteFlex);

        if ((limiteVotorantim && decimalLimiteVot > limite) || (limiteFlex && decimalLimiteFlex > limiteF)) {
            if (!values || values.length === 0) {
                return false;
            }
        }

        return true;
    }

    const toCheckDocumentsSize = (values) => {
        if(values?.length == 0) return true;

        const totalSize = (values ?? []).reduce((acc, cur) => {
            acc += (cur?.file?.size ?? 0);
            return acc;
        }, 0);

        if((totalSize / (1024 * 1024)) > maximumSize) return false;

        return true;
    }

const getValidationSchema = ({ limiteCPP, limitePF, cliente }) =>
  object({
    documentos: yup
      .array()
      .test({
        name: "documentosValidation",
        message: "Erro na validação dos documentos.",
              test: function (values, context) {
                 const { limiteVotorantim, limiteFlex } = context.parent;

        const decimalLimiteVot = toDecimalByString(limiteVotorantim);
        const decimalLimitePF = toDecimalByString(limiteFlex);

        // 1. Limite Votorantim acima do limite
        if (decimalLimiteVot > limite) {
          if (!values || values.length === 0) {
            return this.createError({
              message: `O envio de documentos é obrigatório para valores de limite Votorantim acima de ${money(limite)}`,
              path: this.path,
            });
          }
        }

        // 2. Limite PagueFlex acima do limite
        if (decimalLimitePF > limiteF) {
          if (!values || values.length === 0) {
            return this.createError({
              message: `O envio de documentos é obrigatório para valores de limite PagueFlex acima de ${money(limiteF)}`,
              path: this.path,
            });
          }
        }

        // 3. NOVA REGRA: Pessoa Física - obrigatoriedade por segmento
        const totalSolicitado = decimalLimiteVot + decimalLimitePF;
        const segmentoId = cliente?.segmentoId;

        const limiteSegmento = segmentoId === 8 ? limiteCPP : limitePF;
        if ((cliente?.tipo === 1 || cliente?.tipo === 4) && totalSolicitado > limiteSegmento) {
          if (!values || values.length === 0) {
            return this.createError({
              message: `O envio de documentos é obrigatório para valores acima de ${money(limiteSegmento)} neste segmento.`,
              path: this.path,
            });
          }
        }

        // 4. Limite acima de zero (regra já existente)
        if (!toCheckRequiredFields(context)) {
          return this.createError({
            message: "Um dos limites deve estar acima de zero para prosseguir com o envio.",
            path: this.path,
          });
        }

        // 5. Validação de tamanho dos documentos
        if (!toCheckDocumentsSize(values)) {
          return this.createError({
            message: `O limite máximo para upload é de ${maximumSize}mb. O limite foi atingido, e será necessário reduzir o tamanho dos arquivos`,
            path: this.path,
          });
        }

        // 6. Verificação de documentos obrigatórios
        if (!requiredDocuments(context, values)) {
          return this.createError({
            message: "A apresentação de documentos é obrigatória nas solicitações cujo valor ultrapasse " + money(limite),
            path: this.path,
          });
        }

        return true;
      },
    }),
});

    const [initialValues, setinitialValues] = useState({
        limiteVotorantim: 0,
        limiteFlex: 0,
        limiteVloz: 0,
        documentos: [],
    });

    return {
        getValidationSchema,
        allowedExtensions,
        initialValues,
        valuess,
        setValue,
        validateFile,
        validateLimits
    };
};

export default useCredit;